/**
 * PressureAnnotationLayer.tsx — نصوص خريطة الضغط فوق الخطوط المرسومة بالشيدر.
 *
 * ── ما الذي حلّ محلّ ماذا ─────────────────────────────────────────────────────
 *
 * سلفُ هذا الملفّ (`IsobarLayer`) كان يرسم **الخطوط نفسها**: هندسة GeoJSON مبنيّة
 * بـMarching Squares في عامل حسابي، بمصدر `tolerance=0` كي لا تُسقط MapLibre الحلقات
 * الصغيرة. أُزيل المسار كلّه. الخطوط الآن تُرسم تحليلياً لكل بكسل في `IsobarGLLayer`:
 * تُقاس مسافة البكسل إلى أقرب مستوى بميل الحقل فيخرج الخطّ مباشرةً — بلا تتبّع، وبلا
 * تبسيط، وبلا عاملٍ يُعيد الحساب كل ساعة.
 *
 * فما بقي هنا هو النصّ وحده: `H`/`L` وقيمهما، وأرقام القيم على الخطوط. وهو الجزء
 * الذي **يجب** أن يبقى على المعالج: النصّ يحتاج قراراً بالترتيب والتصادم، ولا يملك
 * الشيدر أن يعرف أين وضع جارُه حرفاً.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Source, Layer } from 'react-map-gl/maplibre';
import type { FeatureCollection } from 'geojson';
import { useMapRef } from './MapContext';
import { usePressureField, type PressureField } from './hooks/usePressureField';
import { getIsobarShown, subscribeIsobarShown } from './webgl/IsobarGLLayer';
import { findIsobarLabels, findPressureCentres, isobarInterval } from './pressureAnnotations';

interface Props {
    timeIndex: number;
    /** مجلّد النموذج (‏rasters/ أو rasters/ecmwf/). */
    dir: string;
    enabled: boolean;
    /** يوفّر النموذج نظير الضغط 16-بت؟ */
    p16: boolean;
    /** ألوان الطبقة جاهزة؟ قبلها لا نبدأ أي جلب كي لا نزاحم الإحماء. */
    ready?: boolean;
    /** «قيم تسميات» — أرقام القيم على الخطوط (مفتاح مستقلّ كما عند Zoom Earth). */
    showValues?: boolean;
    /** عدد ساعات الدورة — حدٌّ لاستباق الشبكة التالية فلا تُطلب ساعة لا وجود لها. */
    frameCount?: number;
}

/**
 * السطر الفاصل بين الحرف وقيمته داخل تعبير `format` — يُبنى برمجياً لا كهروبٍ نصّي.
 * أدوات التحرير الآلي تُحوّل الهروب المكتوب داخل السلسلة إلى سطر حقيقي فتكسر الملفّ،
 * وهذه صيغة لا تقبل التحوير.
 */
const NEWLINE = String.fromCharCode(10);

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

export function PressureAnnotationLayer({ timeIndex, dir, enabled, p16, ready = true, showValues = true, frameCount = 0 }: Props) {
    const mapRef = useMapRef();
    /*
     * ── الأرقام تتبع ما ترسمه الخطوط **فعلاً**، لا الشريط ────────────────────────
     *
     * كانت تُحسب من ساعة الشريط بجلبٍ مستقلّ عن نسيج الخطوط، فيجهز أحدهما قبل الآخر:
     * أرقام بلا خطوط، أو خطوط بلا أرقام، وأرقامٌ تمزج نحو الساعة التالية بينما الخطوط
     * ما زالت على الحالية (نسيجها التالي لم يصل) فتنفكّ عنها (تسجيل 2026-09-26).
     * الآن مصدرها getIsobarShown: الإطار المرسوم، والتالي إن مُزج فعلاً، والنسبة نفسها.
     */
    const [shownUrls, setShownUrls] = useState(() => {
        const s0 = getIsobarShown(); return { url: s0.url, next: s0.next };
    });
    const blendRef = useRef(getIsobarShown().blend);
    const idxOf = (u: string | null) => {
        const m = u ? /_(\d{3})\.png/.exec(u) : null;
        return m ? Number(m[1]) : null;
    };
    const shownIdx = idxOf(shownUrls.url);
    const shownNextIdx = idxOf(shownUrls.next);
    const field = usePressureField(shownIdx ?? timeIndex, dir, enabled && ready && shownIdx !== null, p16, frameCount);
    const fieldNext = usePressureField(shownNextIdx ?? timeIndex, dir, enabled && ready && shownNextIdx !== null, p16, 0);
    const [data, setData] = useState<FeatureCollection>(EMPTY);
    // آخر نتيجة تبقى معروضة أثناء إعادة الحساب: إفراغ المصدر ثم ملؤه يومض الحروف
    // عند كل تحريك، وهو أسوأ من نصف ثانية بحروف متأخّرة بكسلات عن مواضعها.
    const rafRef = useRef(0);
    const timerRef = useRef(0);

    const showValuesRef = useRef(showValues); showValuesRef.current = showValues;

    useEffect(() => subscribeIsobarShown((sh) => {
        blendRef.current = sh.blend;
        setShownUrls((prev) => (prev.url === sh.url && prev.next === sh.next ? prev : { url: sh.url, next: sh.next }));
    }), []);

    // لا خطوط مرسومة ⇒ الخطّافان مُطفآن ⇒ field = null ⇒ لا أرقام. والتالي لم يصل ⇒ لا مزج.

    useEffect(() => {
        if (!enabled || !field) { setData(EMPTY); return; }
        // مصفوفة المزج تُعاد استعمالها: الحقل قرابة مليون قيمة، وتخصيصها ثماني مرّات في
        // الثانية يعني ضغطاً على جامع القمامة يُرى تقطيعاً.
        let mixBuf: Float32Array | null = null;
        let lastBlend = -1;
        const blended = (): PressureField => {
            const f = fieldNext && fieldNext.width === field.width && fieldNext.height === field.height
                ? blendRef.current : 0;
            lastBlend = f;
            if (f <= 0.001 || !fieldNext) return field;
            if (f >= 0.999) return fieldNext;
            const a = field.values, b = fieldNext.values;
            if (!mixBuf || mixBuf.length !== a.length) mixBuf = new Float32Array(a.length);
            for (let i = 0; i < a.length; i++) mixBuf[i] = a[i] + (b[i] - a[i]) * f;
            return { ...field, values: mixBuf };
        };
        const wrap = mapRef.current;
        if (!wrap) return;
        const map = wrap.getMap();

        const compute = () => {
            const canvas = map.getCanvas();
            const width = canvas.clientWidth, height = canvas.clientHeight;
            const interval = isobarInterval(map.getZoom());
            const fld = blended();
            const centres = findPressureCentres(fld, map, width, height, interval);
            const labels = showValuesRef.current
                ? findIsobarLabels(fld, map, width, height, interval, centres)
                : [];
            setData({
                type: 'FeatureCollection',
                features: [
                    ...centres.map((c) => ({
                        type: 'Feature' as const,
                        properties: { role: 'centre', kind: c.kind, value: c.value },
                        geometry: { type: 'Point' as const, coordinates: [c.lon, c.lat] },
                    })),
                    ...labels.map((l) => ({
                        type: 'Feature' as const,
                        properties: { role: 'label', text: l.text, rotate: l.rotate },
                        geometry: { type: 'Point' as const, coordinates: [l.lon, l.lat] },
                    })),
                ],
            });
        };

        /*
         * الخنق 300 مللي — رقم Zoom Earth نفسه (‏nextMarkerUpdateTime).
         *
         * الحساب يقرأ الحقل المموّه في آلاف العقد، وهو أرخص من أن يُلاحظ مرّةً وأغلى
         * من أن يُعاد ستّين مرّة في الثانية أثناء السحب. والنتيجة أثناء التحريك تبقى
         * صحيحةً جغرافياً على أي حال — المعالم مربوطة بإحداثيات لا ببكسلات — فكل ما
         * يتأخّر هو **اختيار** أيّها يُعرض، وهو ما لا يُرى تأخّره.
         */
        const schedule = () => {
            if (timerRef.current) return;
            timerRef.current = window.setTimeout(() => {
                timerRef.current = 0;
                cancelAnimationFrame(rafRef.current);
                rafRef.current = requestAnimationFrame(compute);
            }, 300);
        };

        compute();
        map.on('move', schedule);
        map.on('zoom', schedule);
        // أثناء التشغيل: إعادة حساب كل ~120 مللي (لا كل إطار) — الخطّ يتحرّك ببطء داخل
        // الساعة، فثماني خطوات في الثانية تُبقي الرقم عليه دون كلفة ستّين حساباً.
        let lastRun = 0;
        const unsubscribe = subscribeIsobarShown((sh) => {
            const f = sh.blend;
            if (Math.abs(f - lastBlend) < 0.01) return;
            const now = performance.now();
            if (now - lastRun < 120) return;
            lastRun = now;
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(compute);
        });
        return () => {
            unsubscribe();
            map.off('move', schedule);
            map.off('zoom', schedule);
            window.clearTimeout(timerRef.current); timerRef.current = 0;
            cancelAnimationFrame(rafRef.current);
        };
    }, [mapRef, field, fieldNext, enabled, showValues]);

    const centreFilter = useMemo(() => ['==', ['get', 'role'], 'centre'] as unknown as never, []);
    const labelFilter = useMemo(() => ['==', ['get', 'role'], 'label'] as unknown as never, []);

    if (!enabled) return null;

    return (
        <Source id="pressure-annotations" type="geojson" data={data} tolerance={0} buffer={64}>
            {/*
              * ── أرقام القيم: على الخطّ نفسه، وبلونه ────────────────────────────
              *
              * الرقم يُوضع عند نقطة عبورٍ فعلية للمستوى ويدور مع الخطّ، فيُقرأ على
              * امتداده لا عارضاً له. ولونه أبيض كالخطّ تماماً — عند Zoom Earth
              * يُرسم الرقمُ والخطّ بالإضافة اللونية نفسها (‏+0.5) فيبدوان شيئاً
              * واحداً: الخطّ يتّسع فيصير رقماً ثم يعود خطّاً.
              *
              * والهالة داكنة خفيفة (‏0.3) لا لتلوين النصّ بل لفصل حرفٍ أبيض عن خطٍّ
              * أبيض يمرّ تحته — بلا ذلك يذوب الرقم في الخطّ عند كل تقاطع، وهو ما
              * يعالجه Zoom Earth بإزاحة التسمية في تخطيطٍ لا نملكه في MapLibre.
              */}
            <Layer
                id="pressure-value-labels"
                type="symbol"
                filter={labelFilter}
                layout={{
                    'text-field': ['get', 'text'],
                    'text-font': ['Open Sans Bold'],
                    'text-size': 11,
                    'text-rotate': ['get', 'rotate'],
                    'text-rotation-alignment': 'map',
                    'text-pitch-alignment': 'map',
                    'text-allow-overlap': false,
                    'text-padding': 3,
                    'symbol-placement': 'point',
                }}
                paint={{
                    'text-color': '#ffffff',
                    'text-halo-color': 'rgba(0,0,0,0.3)',
                    'text-halo-width': 1.1,
                }}
            />
            {/*
              * الحرف فوق والقيمة تحته — تركيب Zoom Earth نفسه. والمقاس يتبع التقريب لا
              * رقماً ثابتاً: شاشة الجوّال تعرض مساحةً أقلّ بالدرجات فتتزاحم الحروف عليها،
              * والربط بالتقريب يعالج ذلك بلا استثناء خاصّ بالجوّال يُنسى عند أول تعديل.
              */}
            <Layer
                id="pressure-centres"
                type="symbol"
                filter={centreFilter}
                layout={{
                    'text-field': ['format',
                        ['get', 'kind'], { 'font-scale': 1 },
                        NEWLINE, {},
                        ['to-string', ['get', 'value']], { 'font-scale': 0.68 }],
                    'text-font': ['Open Sans Bold'],
                    'text-size': ['interpolate', ['linear'], ['zoom'], 1, 13, 8, 26],
                    'text-line-height': 1.05,
                    'text-allow-overlap': false,
                    'text-padding': 6,
                }}
                paint={{
                    'text-color': '#ffffff',
                    'text-halo-color': 'rgba(0,0,0,0.6)',
                    'text-halo-width': 1.6,
                }}
            />
        </Source>
    );
}
